// JURÍDICO — incluir contrato já assinado (2026-08-21).
//
// A pasta de contratos assinados não entra no sistema digitando 15 fichas.
// Aqui o caminho é: solta os PDFs → a IA lê todos ao mesmo tempo → você confere
// um a um e confirma. Enquanto você confere o primeiro, os outros já estão
// sendo lidos.
//
// 🚨 O formulário chega PREENCHIDO, não SALVO. Nada vira ficha antes de alguém
//    clicar em arquivar: leitura de PDF erra data e valor, e data de vigência
//    errada vira contrato que se renova sozinho — o caso caro que este módulo
//    existe para evitar. Por isso os campos que vieram do PDF ficam marcados:
//    é o que merece um segundo olhar.
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert, App as AntApp, Button, Checkbox, DatePicker, Flex, Form, Input, InputNumber,
  Modal, Select, Space, Spin, Tag, Tooltip, Typography, Upload,
} from 'antd';
import { InboxOutlined, FileTextOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
// 🚨 O upload vai pela porta do JURÍDICO, não pela do acervo: lá o porteiro é
// a permissão de VER a categoria (o módulo inteiro), e o coordenador — que o
// Lucas mandou poder anexar — morria no primeiro passo.
import { subirContratoAssinado } from './api';
import {
  lerContrato, statusLeitura, acervarContrato, listarContratosOpDoCliente,
  RENOVACAO_CFG, SITUACAO_CFG, LeituraContrato,
  ContratoOpOpcao, listarTiposContrato, TipoContrato, TIPO_DISTRATO, opcoesPapel,
} from './api';

const { Text, Paragraph } = Typography;

type Etapa = 'espera' | 'subindo' | 'lendo' | 'pronto' | 'erro' | 'arquivado' | 'adiado';

interface Item {
  uid: string;
  file: File;
  nome: string;
  etapa: Etapa;
  s3_key?: string;
  leitura_id?: number;
  campos?: Record<string, any>;
  sumario_md?: string | null;
  fonte?: LeituraContrato['fonte'];
  erro?: string;
}

const MAX_ARQUIVOS = 10;
// Leitura de contrato longo leva ~20-60 s. Depois de 4 minutos alguma coisa
// morreu no caminho, e girar para sempre é pior que dizer "não deu".
const TETO_ESPERA_MS = 4 * 60 * 1000;

const ETAPA_CFG: Record<Etapa, { rotulo: string; cor?: string }> = {
  espera:    { rotulo: 'na fila' },
  subindo:   { rotulo: 'enviando', cor: 'blue' },
  lendo:     { rotulo: 'lendo', cor: 'blue' },
  pronto:    { rotulo: 'confira', cor: 'gold' },
  erro:      { rotulo: 'falhou', cor: 'red' },
  arquivado: { rotulo: 'arquivado', cor: 'green' },
  adiado:    { rotulo: 'adiado' },
};

/** Quando o modal é aberto DE DENTRO de um contrato do operacional, estes
 *  dados vêm do sistema e não se discutem: a contraparte é o cliente do
 *  projeto, o papel é `contratada` (alguém nos contratou) e o valor é o
 *  contratado. A IA continua lendo vigência, renovação, reajuste e foro — que
 *  é o que só está no PDF. */
export interface ContextoVenda {
  contrato_op_id: string;
  numero: string;
  titulo: string;
  cliente_id?: number | null;
  cliente_nome?: string | null;
  valor_contratado?: number | string | null;
}

export function ModalIncluirAssinado({ aberto, onFechar, onArquivado, venda }: {
  aberto: boolean;
  onFechar: () => void;
  onArquivado: () => void;
  venda?: ContextoVenda;
}) {
  const { message } = AntApp.useApp();
  const [form] = Form.useForm();

  const [itens, setItens] = useState<Item[]>([]);
  const [atualUid, setAtualUid] = useState<string | null>(null);
  const [salvando, setSalvando] = useState(false);
  // GUARDA-CHUVA (2026-08-25): os contratos do cliente, para dizer o que este
  // instrumento cobre. O contrato da Nova Engevix chegou meses depois citando
  // 5 propostas — no sistema, o contrato-pai e os aditivos dele.
  const [opcoesOp, setOpcoesOp] = useState<ContratoOpOpcao[]>([]);
  const [carregandoOp, setCarregandoOp] = useState(false);
  const [erroOp, setErroOp] = useState(false);
  // 🚨 `beforeUpload` do antd roda UMA VEZ POR ARQUIVO, cada uma com a leva
  // inteira: sem trava, soltar 5 contratos disparava `iniciar` 5 vezes — 25
  // uploads no S3 e 25 leituras do Bedrock (cobradas por chamada), enquanto a
  // tela mostrava só 5, porque o último setItens vencia. Descoberto em 01/09
  // ao montar o lote de monografias, que sofria do mesmo mal.
  const loteRef = useRef<File[] | null>(null);
  // TIPOS DE CONTRATO (2026-09-15): a lista vem do CATÁLOGO (o mútuo cadastrado
  // pela tela aparece aqui sozinho). Antes era um objeto fixo no código, e o
  // servidor trocava tipo desconhecido por "Outro contrato" em silêncio.
  const [tipos, setTipos] = useState<TipoContrato[]>([]);
  const [erroTipos, setErroTipos] = useState(false);
  const [tentativaTipos, setTentativaTipos] = useState(0);
  const tipoSelId = Form.useWatch('tipo_id', form);
  const tipoSel = tipos.find((t) => t.id === tipoSelId) || null;
  const itensRef = useRef<Item[]>([]);
  const inicioRef = useRef<number>(0);
  itensRef.current = itens;

  const atual = itens.find((i) => i.uid === atualUid) || null;
  const arquivados = itens.filter((i) => i.etapa === 'arquivado').length;
  const lendo = itens.some((i) => i.etapa === 'subindo' || i.etapa === 'lendo');

  const alterar = useCallback((uid: string, patch: Partial<Item>) => {
    setItens((antes) => antes.map((i) => (i.uid === uid ? { ...i, ...patch } : i)));
  }, []);

  function zerar() {
    loteRef.current = null;     // libera a leva: repetir os MESMOS arquivos vale
    setItens([]);
    setAtualUid(null);
    form.resetFields();
  }

  // ── Subir e mandar ler ───────────────────────────────────────────────────
  // O upload é serial (um PUT grande de cada vez economiza a rede do escritório
  // de campo), mas a LEITURA de cada um dispara assim que o arquivo chega: são
  // Lambdas independentes, e esperar em fila só faria o Lucas esperar mais.
  async function iniciar(arquivos: File[]) {
    const novos: Item[] = arquivos.slice(0, MAX_ARQUIVOS).map((f, n) => ({
      uid: `${Date.now()}-${n}-${f.name}`,
      file: f, nome: f.name, etapa: 'espera' as Etapa,
    }));
    if (arquivos.length > MAX_ARQUIVOS) {
      message.warning(`Peguei os primeiros ${MAX_ARQUIVOS}. Depois é só repetir com o resto.`);
    }
    setItens(novos);
    setAtualUid(null);
    inicioRef.current = Date.now();

    for (const it of novos) {
      try {
        alterar(it.uid, { etapa: 'subindo' });
        const s3_key = await subirContratoAssinado(it.file);
        alterar(it.uid, { s3_key });
        const r = await lerContrato({ s3_key, arquivo_nome: it.nome });
        alterar(it.uid, { etapa: 'lendo', leitura_id: r.leitura_id });
      } catch (e: any) {
        alterar(it.uid, { etapa: 'erro', erro: e.response?.data?.error || e.message });
      }
    }
  }

  useEffect(() => {
    if (!aberto) return;
    let vivo = true;
    setErroTipos(false);
    listarTiposContrato()
      // Distrato tem porta própria (o botão "Distrato" do contrato).
      .then((r) => { if (vivo) setTipos((r.tipos || []).filter((t) => t.codigo !== TIPO_DISTRATO)); })
      // Falha aqui não pode virar lista vazia calada: sem tipo, o arquivamento
      // cairia em "Outro contrato" e o sigilo do tipo não alcançaria o PDF.
      .catch(() => { if (vivo) setErroTipos(true); });
    return () => { vivo = false; };
  }, [aberto, tentativaTipos]);

  // O tipo de partida: o do projeto (venda = prestação de serviço), o que a IA
  // leu, ou "Outro contrato".
  const tipoInicial = useCallback((campos?: Record<string, any>) => {
    const porCodigo = (codigo: string) => tipos.find((t) => t.codigo === codigo)?.id;
    if (venda) return porCodigo('contrato_servico');
    if (tipos.some((t) => t.id === campos?.tipo_id)) return campos?.tipo_id as number;
    return porCodigo('contrato_outro');
  }, [tipos, venda]);

  // A lista de tipos pode chegar DEPOIS da primeira leitura: aí o campo ainda
  // está vazio e recebe o tipo de partida — nunca por cima do que já está lá.
  useEffect(() => {
    if (!atual?.campos || !tipos.length || form.getFieldValue('tipo_id')) return;
    const inicial = tipoInicial(atual.campos);
    if (inicial) form.setFieldValue('tipo_id', inicial);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tipos]);

  // ── "Já leu?" ────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!aberto) return;
    const id = setInterval(async () => {
      const pendentes = itensRef.current.filter((i) => i.etapa === 'lendo' && i.leitura_id);
      if (!pendentes.length) return;
      if (Date.now() - inicioRef.current > TETO_ESPERA_MS) {
        pendentes.forEach((p) => alterar(p.uid, {
          etapa: 'erro',
          erro: 'A leitura demorou demais. Tente de novo ou cadastre pelo acervo.',
        }));
        return;
      }
      await Promise.all(pendentes.map(async (p) => {
        try {
          const { leitura } = await statusLeitura(p.leitura_id!);
          if (leitura.status === 'pronto') {
            alterar(p.uid, {
              etapa: 'pronto', campos: leitura.campos || {},
              sumario_md: leitura.sumario_md, fonte: leitura.fonte,
            });
          } else if (leitura.status === 'erro') {
            alterar(p.uid, { etapa: 'erro', erro: leitura.erro || 'Não consegui ler esse arquivo.' });
          }
        } catch { /* rede oscilou: tenta no próximo tique */ }
      }));
    }, 3000);
    return () => clearInterval(id);
  }, [aberto, alterar]);

  // Assim que houver um pronto e nenhum aberto, abre o próximo para conferência.
  useEffect(() => {
    if (atualUid) return;
    const proximo = itens.find((i) => i.etapa === 'pronto');
    if (proximo) setAtualUid(proximo.uid);
  }, [itens, atualUid]);

  // ── Preencher o formulário com o que a IA leu ───────────────────────────
  useEffect(() => {
    if (!atual?.campos) return;
    const c = atual.campos;
    const fim = c.vigencia_fim ? dayjs(c.vigencia_fim) : null;
    form.setFieldsValue({
      // Vindo do contrato do projeto, o que o sistema sabe manda: o nome do
      // contrato, a contraparte e o valor já foram acordados e cadastrados.
      // Deixar a IA sobrescrever isso seria trocar dado conferido por leitura.
      titulo: venda ? `${venda.numero} — ${venda.titulo}`
        : (c.titulo || atual.nome.replace(/\.(pdf|docx)$/i, '')),
      // Cada arquivo da fila começa do SEU tipo (sem herdar o do anterior). Se a
      // lista de tipos ainda não chegou, o efeito dos tipos preenche depois.
      tipo_id: tipoInicial(c),
      contraparte_nome: venda?.cliente_nome || c.contraparte_nome || '',
      contraparte_doc: c.contraparte_doc || '',
      numero_documento: c.numero_documento || '',
      objeto: c.objeto || '',
      nosso_papel: venda ? 'contratada' : (c.nosso_papel || 'contratante'),
      valor: venda?.valor_contratado != null ? Number(venda.valor_contratado) : (c.valor ?? null),
      data_assinatura: c.data_assinatura ? dayjs(c.data_assinatura) : null,
      vigencia_inicio: c.vigencia_inicio ? dayjs(c.vigencia_inicio) : null,
      vigencia_fim: fim,
      renovacao: c.renovacao || 'nao',
      aviso_previa_dias: c.aviso_previa_dias ?? 60,
      reajuste_indice: c.reajuste_indice || undefined,
      foro: c.foro || '',
      // Contrato assinado está em vigor — a menos que a vigência já tenha
      // passado, que é o caso da pasta de contratos antigos entrando de uma vez.
      situacao: fim && fim.isBefore(dayjs(), 'day') ? 'encerrado' : 'vigente',
      vincular_cliente: !!(venda?.cliente_id || c.cliente_id),
      observacoes: (c.pontos_de_atencao || []).length
        ? `Pontos de atenção lidos do contrato:\n- ${(c.pontos_de_atencao as string[]).join('\n- ')}`
        : '',
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [atual, form, venda]);

  // ── Cobertura: carregar os contratos do cliente ─────────────────────────
  // O cliente vem do projeto (quando o modal abriu de um contrato) ou do que a
  // IA casou por CNPJ. Sem cliente não há lista — e o picker some, em vez de
  // mostrar uma caixa vazia sem explicação.
  const clienteEfetivo = venda?.cliente_id ?? atual?.campos?.cliente_id ?? null;
  useEffect(() => {
    if (!aberto || !clienteEfetivo) { setOpcoesOp([]); setErroOp(false); return; }
    let vivo = true;
    // Zera ANTES de buscar: na fila de vários PDFs, o cliente muda a cada
    // conferência, e a lista do anterior seguiria na tela como se fosse desta.
    setOpcoesOp([]); setErroOp(false); setCarregandoOp(true);
    listarContratosOpDoCliente(Number(clienteEfetivo))
      .then((d) => { if (vivo) setOpcoesOp(d?.contratos || []); })
      // Falha silenciosa vira "nenhum projeto deste cliente", que é mentira e
      // faz a pessoa arquivar sem cobertura achando que não havia o que marcar.
      .catch(() => { if (vivo) setErroOp(true); })
      .finally(() => { if (vivo) setCarregandoOp(false); });
    return () => { vivo = false; };
  }, [aberto, clienteEfetivo]);

  // O contrato de onde o modal abriu já vem marcado — é o caso comum (anexar o
  // papel de UM projeto). A pessoa acrescenta os aditivos que o papel cobre.
  useEffect(() => {
    if (!atual) return;
    form.setFieldValue('cobre_contratos_op', venda ? [venda.contrato_op_id] : []);
  }, [atual, form, venda]);

  function proximoDepoisDe(uid: string) {
    const resto = itensRef.current.filter((i) => i.uid !== uid && i.etapa === 'pronto');
    setAtualUid(resto[0]?.uid || null);
  }

  async function arquivar() {
    if (!atual?.leitura_id) return;
    let v: any;
    try { v = await form.validateFields(); } catch { return; }
    setSalvando(true);
    const cobre: string[] = v.cobre_contratos_op || [];
    try {
      await acervarContrato({
        leitura_id: atual.leitura_id,
        titulo: v.titulo,
        tipo_id: v.tipo_id,
        contraparte_nome: v.contraparte_nome,
        contraparte_doc: v.contraparte_doc || null,
        cliente_id: v.vincular_cliente
          ? (venda?.cliente_id ?? atual.campos?.cliente_id ?? null) : null,
        // O "projeto principal" da ficha. 🚨 Respeita o PICKER: se a pessoa
        // tirou o projeto de origem da lista (o papel formaliza só o aditivo),
        // ele NÃO pode voltar como principal — o backend re-inclui o escalar na
        // cobertura, e a tela passaria a mostrar como formalizado um projeto
        // que a pessoa acabou de dizer que este papel não cobre.
        contrato_op_id: cobre.length
          ? (venda && cobre.includes(venda.contrato_op_id) ? venda.contrato_op_id : cobre[0])
          : (venda?.contrato_op_id || null),
        // GUARDA-CHUVA: tudo que este papel cobre. Vazio = o backend não mexe
        // na cobertura (mantém o comportamento antigo de ficha sem projeto).
        cobre_contratos_op: cobre.length ? cobre : undefined,
        numero_documento: v.numero_documento || null,
        objeto: v.objeto || null,
        nosso_papel: v.nosso_papel,
        valor: v.valor ?? null,
        data_assinatura: v.data_assinatura ? dayjs(v.data_assinatura).format('YYYY-MM-DD') : null,
        vigencia_inicio: v.vigencia_inicio ? dayjs(v.vigencia_inicio).format('YYYY-MM-DD') : null,
        vigencia_fim: v.vigencia_fim ? dayjs(v.vigencia_fim).format('YYYY-MM-DD') : null,
        renovacao: v.renovacao,
        aviso_previa_dias: v.aviso_previa_dias,
        reajuste_indice: v.reajuste_indice || null,
        foro: v.foro || null,
        situacao: v.situacao,
        observacoes: v.observacoes || null,
      });
      message.success(`${v.contraparte_nome} — contrato arquivado e ficha criada.`);
      alterar(atual.uid, { etapa: 'arquivado' });
      proximoDepoisDe(atual.uid);
      onArquivado();
    } catch (e: any) {
      // O corpo do erro pode vir embrulhado em `body` (string) — é assim que a
      // layer serializa e o wrapper só desembrulha no caminho feliz.
      const bruto = e.response?.data;
      let d: any = bruto;
      if (bruto?.body) { try { d = JSON.parse(bruto.body); } catch { d = bruto; } }
      // 409 COM ficha_id = este PDF já tem ficha. Beco sem saída antes; agora
      // o caminho é acrescentar o projeto à cobertura da ficha que existe.
      // (O outro 409 do acervar — "esse contrato já foi arquivado" — não traz
      //  ficha_id, e continua sendo mensagem simples.)
      if (e.response?.status === 409 && d?.ficha_id) {
        message.warning('Este PDF já tem ficha no Jurídico. Abra a ficha e '
          + 'acrescente o projeto à cobertura dela — assim um papel só cobre '
          + 'o contrato e os aditivos.');
        alterar(atual.uid, { etapa: 'adiado' });
        proximoDepoisDe(atual.uid);
        onArquivado();
      } else {
        message.error('Falha ao arquivar: ' + (d?.error || e.message));
      }
    } finally { setSalvando(false); }
  }

  function adiar() {
    if (!atual) return;
    alterar(atual.uid, { etapa: 'adiado' });
    proximoDepoisDe(atual.uid);
  }

  function fechar() {
    const pendente = itens.some((i) => ['espera', 'subindo', 'lendo', 'pronto'].includes(i.etapa));
    if (pendente) {
      Modal.confirm({
        title: 'Sair com contratos sem arquivar?',
        content: 'O que já foi arquivado continua no sistema. O resto não foi cadastrado — '
          + 'os arquivos não conferidos terão que ser enviados de novo.',
        okText: 'Sair', cancelText: 'Continuar conferindo',
        onOk: () => { zerar(); onFechar(); },
      });
      return;
    }
    zerar();
    onFechar();
  }

  // Rótulo com a marca do que veio do PDF: é o que merece conferência.
  const rot = (label: string, chave: string) => {
    const veioDaIa = atual?.campos && atual.campos[chave] !== undefined && atual.campos[chave] !== null;
    return (
      <Space size={4}>
        {label}
        {veioDaIa && (
          <Tooltip title="Este campo foi lido do PDF. Confira antes de salvar.">
            <Tag color="gold" style={{ fontSize: 10, lineHeight: '16px', margin: 0 }}>do PDF</Tag>
          </Tooltip>
        )}
      </Space>
    );
  };

  return (
    <Modal
      open={aberto} onCancel={fechar} footer={null}
      title="Incluir contrato já assinado"
      width="94vw" style={{ maxWidth: 1000, top: 16 }}
      styles={{ body: { maxHeight: 'calc(100vh - 130px)', overflowY: 'auto' } }}
      destroyOnHidden
    >
      {!itens.length && (
        <>
          <Paragraph type="secondary" style={{ fontSize: 12 }}>
            {venda
              ? <>O contrato assinado de <b>{venda.numero}</b> vai para o acervo da empresa e
                  a ficha nasce ligada a este projeto. A IA lê o PDF para preencher vigência,
                  renovação, reajuste e foro — o resto já vem do contrato.</>
              : <>Vale para qualquer contrato que já exista só como arquivo — nosso, assinado
                  fora do sistema, ou de terceiro. O PDF vai para o acervo da empresa e a ficha
                  entra no controle de vigências (é ela que dispara o aviso de segunda-feira).</>}
          </Paragraph>
          {erroTipos && (
            // Sem a lista de tipos, soltar o PDF levaria à leitura (paga) de
            // um contrato que não teria como ser arquivado no tipo certo.
            <Alert type="warning" showIcon style={{ marginBottom: 8 }}
              message="Não consegui carregar os tipos de contrato."
              action={<Button size="small" onClick={() => setTentativaTipos((n) => n + 1)}>
                Tentar de novo</Button>} />
          )}
          <Upload.Dragger
            disabled={erroTipos}
            multiple={!venda} accept=".pdf,.docx" showUploadList={false}
            beforeUpload={(_f, lista) => {
              if (loteRef.current === (lista as File[])) return false;   // mesma leva
              loteRef.current = lista as File[];
              iniciar(lista as File[]);
              return false;
            }}
          >
            <p className="ant-upload-drag-icon"><InboxOutlined /></p>
            <p className="ant-upload-text">Solte aqui os contratos assinados</p>
            <p className="ant-upload-hint">
              PDF ou Word, até {MAX_ARQUIVOS} por vez. A leitura de cada um leva de 20 a 60
              segundos — os outros vão sendo lidos enquanto você confere o primeiro.
            </p>
          </Upload.Dragger>
        </>
      )}

      {!!itens.length && (
        <Flex gap={16} wrap align="flex-start">
          {itens.length > 1 && (
            <div style={{ flex: '0 0 220px', minWidth: 200 }}>
              <Text type="secondary" style={{ fontSize: 11 }}>
                {arquivados} de {itens.length} arquivados
              </Text>
              <div style={{ marginTop: 8 }}>
                {itens.map((i) => (
                  <div key={i.uid}
                    onClick={() => { if (i.etapa === 'pronto') setAtualUid(i.uid); }}
                    style={{
                      padding: '6px 8px', borderRadius: 6, marginBottom: 4,
                      cursor: i.etapa === 'pronto' ? 'pointer' : 'default',
                      background: i.uid === atualUid ? 'rgba(22,119,255,0.08)' : undefined,
                    }}>
                    <Space size={4} align="start">
                      <FileTextOutlined style={{ fontSize: 12, marginTop: 3 }} />
                      <Space direction="vertical" size={0}>
                        <Text style={{ fontSize: 12 }} ellipsis>{i.nome}</Text>
                        <Tag color={ETAPA_CFG[i.etapa].cor} style={{ fontSize: 10, margin: 0 }}>
                          {ETAPA_CFG[i.etapa].rotulo}
                        </Tag>
                      </Space>
                    </Space>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div style={{ flex: '1 1 420px', minWidth: 320 }}>
            {!atual && lendo && (
              <Flex vertical align="center" gap={10} style={{ padding: '48px 0' }}>
                <Spin />
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Lendo o contrato… leva de 20 a 60 segundos.
                </Text>
              </Flex>
            )}

            {!atual && !lendo && (
              <Flex vertical align="center" gap={10} style={{ padding: '32px 0' }}>
                <Text>
                  {arquivados ? `${arquivados} contrato(s) arquivado(s).` : 'Nada para conferir.'}
                </Text>
                {itens.some((i) => i.etapa === 'erro') && (
                  <Alert type="warning" showIcon style={{ width: '100%' }}
                    message="Alguns arquivos não foram lidos"
                    description={
                      <ul style={{ margin: 0, paddingLeft: 18 }}>
                        {itens.filter((i) => i.etapa === 'erro').map((i) => (
                          <li key={i.uid} style={{ fontSize: 12 }}>
                            <b>{i.nome}</b> — {i.erro}
                          </li>
                        ))}
                      </ul>
                    } />
                )}
                <Space>
                  <Button onClick={zerar}>Enviar outros contratos</Button>
                  <Button type="primary" onClick={fechar}>Fechar</Button>
                </Space>
              </Flex>
            )}

            {atual && (
              <>
                <Alert
                  type="warning" showIcon style={{ marginBottom: 12 }}
                  message={`Confira antes de arquivar — ${atual.nome}`}
                  description={
                    <span style={{ fontSize: 12 }}>
                      O que está marcado com <Tag color="gold" style={{ fontSize: 10 }}>do PDF</Tag>
                      foi lido pela IA e pode estar errado. Olhe principalmente <b>datas de
                      vigência</b> e <b>valor</b>: é o que decide quando o contrato aparece no
                      aviso de segunda-feira.
                      {atual.fonte === 'imagem' && (
                        <> Este PDF é <b>digitalizado</b> — número lido de imagem erra mais.</>
                      )}
                    </span>
                  }
                />

                <Form form={form} layout="vertical" size="small">
                  {/* O casamento com o cliente é sugestão: casou por CNPJ (não
                      erra) ou por razão social exata. Fica dentro do Form para
                      ser um campo como os outros — e desmarcável. */}
                  {(venda?.cliente_id || atual.campos?.cliente_id) && (
                    <Form.Item name="vincular_cliente" valuePropName="checked">
                      <Checkbox>
                        <Text style={{ fontSize: 12 }}>
                          Vincular ao cliente{' '}
                          <b>{venda?.cliente_nome || atual.campos?.cliente_nome}</b>{' '}
                          <Text type="secondary" style={{ fontSize: 11 }}>
                            ({venda ? 'cliente do projeto'
                              : atual.campos?.cliente_casou_por === 'documento'
                                ? 'casou pelo CNPJ/CPF' : 'casou pelo nome'})
                          </Text>
                        </Text>
                      </Checkbox>
                    </Form.Item>
                  )}
                  {/* GUARDA-CHUVA (2026-08-25): um papel só pode cobrir vários
                      contratos. O da Nova Engevix cita 5 propostas — aqui, o
                      contrato-pai e os aditivos dele. Sem cliente não há lista,
                      e o campo some em vez de aparecer vazio sem explicação. */}
                  {!!clienteEfetivo && (
                    <Form.Item
                      name="cobre_contratos_op"
                      label={
                        <Text style={{ fontSize: 12 }}>
                          Este contrato cobre quais projetos?{' '}
                          <Text type="secondary" style={{ fontSize: 11 }}>
                            (o instrumento que chega depois costuma cobrir o
                            contrato e os aditivos de uma vez)
                          </Text>
                        </Text>
                      }>
                      <Select
                        mode="multiple"
                        allowClear
                        showSearch
                        optionFilterProp="label"
                        loading={carregandoOp}
                        placeholder={carregandoOp ? 'Carregando os projetos do cliente…'
                          : 'Escolha os projetos que este papel formaliza'}
                        notFoundContent={carregandoOp ? <Spin size="small" />
                          : erroOp ? 'Não consegui carregar os projetos — tente de novo'
                          : 'Nenhum projeto deste cliente'}
                        status={erroOp ? 'warning' : undefined}
                        options={opcoesOp.map((o) => ({
                          value: o.id,
                          // `label` é o que a busca filtra: número + título.
                          label: `${o.numero} — ${o.titulo}`,
                          title: o.coberto_por
                            ? `Já coberto pelo instrumento de ${o.coberto_por_nome || 'outra ficha'}`
                            : undefined,
                        }))}
                        optionRender={(op) => {
                          const o = opcoesOp.find((x) => x.id === op.value);
                          return (
                            <Flex vertical gap={0}>
                              <Text style={{ fontSize: 12 }}>
                                {o?.pai_numero && (
                                  <Text type="secondary" style={{ fontSize: 11 }}>
                                    aditivo de {o.pai_numero} ·{' '}
                                  </Text>
                                )}
                                <b>{o?.numero}</b> — {o?.titulo}
                              </Text>
                              {/* Avisa ANTES de marcar: o backend recusaria o
                                  contrato de outro cliente, e cobrir de novo o
                                  que já está coberto costuma ser engano. */}
                              {!!o?.coberto_por && (
                                <Text type="warning" style={{ fontSize: 11 }}>
                                  já coberto por outro instrumento
                                  {o.coberto_por_nome ? ` (${o.coberto_por_nome})` : ''}
                                </Text>
                              )}
                            </Flex>
                          );
                        }}
                      />
                    </Form.Item>
                  )}
                  <Flex gap={12} wrap>
                    <Form.Item name="titulo" label={rot('Título', 'titulo')}
                      rules={[{ required: true, message: 'Dê um nome ao contrato' }]}
                      style={{ flex: '1 1 320px' }}>
                      <Input placeholder="Ex.: Locação da sala 302 — Ed. Prime" />
                    </Form.Item>
                    <Form.Item name="tipo_id" label={rot('Tipo', 'tipo_id')}
                      rules={[{ required: true, message: 'Escolha o tipo do contrato' }]}
                      help={erroTipos ? <span>Não consegui carregar os tipos — <Button type="link" size="small"
                        style={{ padding: 0, height: 'auto' }}
                        onClick={() => setTentativaTipos((n) => n + 1)}>tentar de novo</Button></span> : undefined}
                      validateStatus={erroTipos ? 'warning' : undefined}
                      style={{ flex: '0 0 240px' }}>
                      <Select showSearch optionFilterProp="label"
                        options={tipos.map((t) => ({ value: t.id, label: t.nome }))} />
                    </Form.Item>
                  </Flex>

                  <Flex gap={12} wrap>
                    <Form.Item name="contraparte_nome" label={rot('Contraparte', 'contraparte_nome')}
                      rules={[{ required: true, message: 'Informe a contraparte' }]}
                      style={{ flex: '1 1 280px' }}>
                      <Input placeholder="Razão social ou nome" />
                    </Form.Item>
                    <Form.Item name="contraparte_doc" label={rot('CNPJ / CPF', 'contraparte_doc')}
                      style={{ flex: '0 0 170px' }}>
                      <Input />
                    </Form.Item>
                    <Form.Item name="numero_documento" label={rot('Nº do contrato', 'numero_documento')}
                      tooltip="O número que a contraparte deu. O nosso acervo gera o dele."
                      style={{ flex: '0 0 160px' }}>
                      <Input />
                    </Form.Item>
                  </Flex>

                  <Form.Item name="objeto" label={rot('Objeto', 'objeto')}>
                    <Input.TextArea rows={2} />
                  </Form.Item>

                  <Flex gap={12} wrap>
                    {/* O nome de cada lado vem do tipo: num mútuo, "Somos Mutuante". */}
                    <Form.Item name="nosso_papel" label={rot('Nosso papel', 'nosso_papel')}
                      style={{ flex: '1 1 200px' }}>
                      <Select options={opcoesPapel(tipoSel)} />
                    </Form.Item>
                    <Form.Item name="valor" label={rot('Valor (R$)', 'valor')}
                      tooltip={atual.campos?.observacao_valor}
                      style={{ flex: '0 0 170px' }}>
                      <InputNumber style={{ width: '100%' }} min={0} decimalSeparator="," />
                    </Form.Item>
                    <Form.Item name="situacao" label="Situação" style={{ flex: '0 0 160px' }}>
                      <Select options={['vigente', 'encerrado', 'rescindido']
                        .map((v) => ({ value: v, label: SITUACAO_CFG[v]?.label || v }))} />
                    </Form.Item>
                  </Flex>
                  {atual.campos?.observacao_valor && (
                    <Text type="secondary" style={{ fontSize: 11, display: 'block', marginTop: -8,
                                                    marginBottom: 10 }}>
                      Sobre o valor, o contrato diz: {atual.campos.observacao_valor}
                    </Text>
                  )}

                  <Flex gap={12} wrap>
                    <Form.Item name="data_assinatura" label={rot('Assinatura', 'data_assinatura')}
                      style={{ flex: '0 0 150px' }}>
                      <DatePicker format="DD/MM/YYYY" style={{ width: '100%' }} />
                    </Form.Item>
                    <Form.Item name="vigencia_inicio" label={rot('Vigência — início', 'vigencia_inicio')}
                      style={{ flex: '0 0 160px' }}>
                      <DatePicker format="DD/MM/YYYY" style={{ width: '100%' }} />
                    </Form.Item>
                    <Form.Item name="vigencia_fim" label={rot('Vigência — fim', 'vigencia_fim')}
                      tooltip={atual.campos?.vigencia_fim_calculada
                        ? 'Calculada a partir do prazo em meses que o contrato dá — confira.'
                        : undefined}
                      style={{ flex: '0 0 160px' }}>
                      <DatePicker format="DD/MM/YYYY" style={{ width: '100%' }} />
                    </Form.Item>
                    <Form.Item name="renovacao" label={rot('Renovação', 'renovacao')}
                      style={{ flex: '1 1 200px' }}>
                      <Select options={Object.entries(RENOVACAO_CFG)
                        .map(([v, l]) => ({ value: v, label: l }))} />
                    </Form.Item>
                  </Flex>

                  <Flex gap={12} wrap>
                    <Form.Item name="aviso_previa_dias"
                      label={rot('Avisar quantos dias antes do fim', 'aviso_previa_dias')}
                      tooltip="É o que decide quando o contrato aparece no e-mail de segunda-feira."
                      style={{ flex: '0 0 230px' }}>
                      <InputNumber style={{ width: '100%' }} min={0} max={365} />
                    </Form.Item>
                    <Form.Item name="reajuste_indice" label={rot('Reajuste', 'reajuste_indice')}
                      style={{ flex: '0 0 150px' }}>
                      <Select allowClear options={['IPCA', 'IGPM', 'INCC', 'INPC']
                        .map((v) => ({ value: v, label: v }))} />
                    </Form.Item>
                    <Form.Item name="foro" label={rot('Foro', 'foro')} style={{ flex: '1 1 200px' }}>
                      <Input placeholder="Porto Alegre/RS" />
                    </Form.Item>
                  </Flex>

                  <Form.Item name="observacoes" label={rot('Observações', 'pontos_de_atencao')}>
                    <Input.TextArea rows={3} />
                  </Form.Item>
                </Form>

                {atual.sumario_md && (
                  <details style={{ marginBottom: 12 }}>
                    <summary style={{ cursor: 'pointer', fontSize: 12, color: '#666' }}>
                      O que a IA entendeu do contrato
                    </summary>
                    <Paragraph style={{ fontSize: 12, whiteSpace: 'pre-wrap', marginTop: 8 }}>
                      {atual.sumario_md}
                    </Paragraph>
                  </details>
                )}

                <Flex justify="space-between" wrap gap={8}>
                  <Button onClick={adiar}>Deixar para depois</Button>
                  <Space>
                    <Button onClick={fechar}>Fechar</Button>
                    <Button type="primary" loading={salvando} onClick={arquivar}>
                      Arquivar e criar a ficha
                    </Button>
                  </Space>
                </Flex>
              </>
            )}
          </div>
        </Flex>
      )}
    </Modal>
  );
}
